import { logAmplitudeEvent } from 'amplitude/amplitude'
import { FilterDrawer } from 'elements/filtering'
import type { ToolbarProps } from 'elements/list/ListTable'
import { BaseTheme, Button, CloseOutlineIcon, FilterIcon, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { memo, useCallback, useMemo, useState } from 'react'
import SearchInput from 'resources/projects/projects3/elements/SearchInput'
import {
  ORDER_BY_PROJECT_FILTERS,
  ORDER_BY_PROJECT_ORDER_OPTIONS,
  ORDER_BY_PROJECT_SORT_OPTIONS,
  ORDER_BY_PROJECT_SUGGESTED_FILTERS,
} from '../filter/orderByProjectFilters'

const ToolbarContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  flexWrap: 'wrap',
  gap: '12px',
})

const FilterCountBadge = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.blue[700],
  color: 'white',
  height: 20,
  width: 20,
  minWidth: 20,
  fontSize: '0.75rem !important',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 400,
  lineHeight: 1,
}))

const FilterButton = styled(Button)(({ theme }) => ({
  height: '40px',
  borderRadius: '8px',
  backgroundColor: 'white',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
}))

const StyledCloseIcon = styled(CloseOutlineIcon)({
  width: '10px !important',
  height: '10px !important',
})

const ClearFiltersButton = styled(Button)({
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

const SearchContainer = styled('div')({
  flex: '1 1 auto',
  minWidth: 250,
  maxWidth: 700,
})

const ButtonsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
})

const ProjectTableToolbar = memo(({ filter, filterPresenter }: ToolbarProps) => {
  const translate = useTranslate()
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const activeFiltersCount = useMemo(() => {
    let count = 0
    const excludedKeys = ['fieldset', 'q']

    Object.keys(filter.filterValues).forEach((key) => {
      if (excludedKeys.includes(key)) return

      const value = filter.filterValues[key]
      if (value === '' || value == null) return

      count++
    })

    return count
  }, [filter.filterValues])

  const handleClearAllFilters = useCallback(() => {
    logAmplitudeEvent(
      'generic_button_clicked',
      {
        key: 'pages.shop.multi_project_ordering',
        action: 'Filtered',
        context: 'cleared',
      },
      'Hardware Navigation Button'
    )
    filterPresenter.setFilters({ fieldset: 'hardware_ordering' })
  }, [filterPresenter])

  const handleSetFilters = useCallback(
    (newFilters: Record<string, unknown>) => {
      logAmplitudeEvent(
        'generic_button_clicked',
        {
          key: 'pages.shop.multi_project_ordering',
          action: 'Filtered',
          context: JSON.stringify(newFilters),
        },
        'Hardware Navigation Button'
      )
      filterPresenter.setFilters({
        fieldset: filter.filterValues.fieldset,
        ...newFilters,
      })
    },
    [filterPresenter, filter.filterValues]
  )

  return (
    <ToolbarContainer>
      <SearchContainer>
        <SearchInput
          filterValues={filter.filterValues}
          setFilters={filterPresenter.setFilters}
          width="100%"
          placeholder="Search by Project Address, Project ID, Customer Name, Customer Email"
        />
      </SearchContainer>
      <ButtonsContainer>
        <FilterButton
          variant="outlined"
          size="small"
          color={activeFiltersCount > 0 ? 'info' : 'default'}
          startIcon={<FilterIcon color={BaseTheme.palette.grey[800]} />}
          endIcon={activeFiltersCount > 0 ? <FilterCountBadge>{activeFiltersCount}</FilterCountBadge> : null}
          onClick={() => setFilterDrawerOpen(true)}
          style={{
            borderColor: activeFiltersCount > 0 ? BaseTheme.palette.blue[700] : undefined,
            color: activeFiltersCount > 0 ? BaseTheme.palette.blue[700] : undefined,
          }}
        >
          {translate('Sort & Filter')}
        </FilterButton>
        {activeFiltersCount > 0 && (
          <ClearFiltersButton
            variant="text"
            size="small"
            color="info"
            onClick={handleClearAllFilters}
            endIcon={<StyledCloseIcon size={12} color={BaseTheme.palette.grey[700]} />}
          >
            {translate('Clear All Filters')}
          </ClearFiltersButton>
        )}
      </ButtonsContainer>
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filterValues={filter.filterValues}
        setFilters={handleSetFilters}
        filterOptions={ORDER_BY_PROJECT_FILTERS}
        suggestedFilters={ORDER_BY_PROJECT_SUGGESTED_FILTERS}
        sortOptions={ORDER_BY_PROJECT_SORT_OPTIONS}
        orderOptions={ORDER_BY_PROJECT_ORDER_OPTIONS}
        title="Sort & Filter"
        disableTracking
      />
    </ToolbarContainer>
  )
})

export default ProjectTableToolbar
